import React, { useId } from 'react';

export interface RadioOption {
    label: string | React.ReactNode;
    value: string;
    description?: string;
    disabled?: boolean;
}

interface RadioGroupProps {
    name: string;
    options: RadioOption[];
    value: string;
    onChange: (value: string) => void;
    label?: string;
    orientation?: 'vertical' | 'horizontal';
    className?: string;
}

const NativeInput = 'input';

/**
 * RadioGroup - Accessible radio button group with label and description support
 * 
 * @example
 * <RadioGroup
 *   name="scheduleView"
 *   label="Schedule View"
 *   options={[
 *     { label: 'List View', value: 'list' },
 *     { label: 'Calendar View', value: 'calendar' }
 *   ]}
 *   value={scheduleView}
 *   onChange={setScheduleView}
 * />
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
    name,
    options,
    value,
    onChange,
    label,
    orientation = 'vertical',
    className = ''
}) => {
    const generatedId = useId();
    const legendId = label ? `${generatedId}-legend` : undefined;

    return (
        <fieldset className={className} aria-labelledby={legendId}>
            {label ? <legend id={legendId} className="mb-2 font-medium text-gray-700 text-sm dark:text-gray-300">
                    {label}
                </legend> : null}
            <div className={`flex ${orientation === 'vertical' ? 'flex-col gap-2' : 'flex-row gap-4'}`}>
                {options.map((option) => {
                    const optionId = `${generatedId}-${option.value}`;

                    return (
                        <div
                            key={option.value}
                            className={`flex cursor-pointer items-center gap-2 ${option.disabled ? 'cursor-not-allowed opacity-50' : ''}
                        `}
                        >
                        <NativeInput
                            id={optionId}
                            type="radio"
                            name={name}
                            value={option.value}
                            checked={value === option.value}
                            onChange={(e) => onChange(e.target.value)}
                            disabled={option.disabled}
                            className="h-4 w-4 border-gray-300 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 dark:border-gray-600 dark:focus:ring-offset-surface-dark"
                        />
                        <div className="flex-1">
                            <label htmlFor={optionId} className="text-gray-900 text-sm dark:text-gray-100">
                                {option.label}
                            </label>
                            {option.description ? <p className="mt-0.5 text-gray-500 text-xs dark:text-gray-400">
                                    {option.description}
                                </p> : null}
                        </div>
                    </div>
                    );
                })}
            </div>
        </fieldset>
    );
};

RadioGroup.displayName = 'RadioGroup';














